import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import {
  VIDA_PROPONENTE_MAINTENANCE_ROUTES,
} from '../../services/requestsService';
import { useUserStore } from '../../store/userStore';
import { useVidaProtocolAtendimentoStore } from '../../store/vida/vidaProtocolServiceStore';
import type { ProponentFormValues } from '../../types/vida/cadastralMaintenance/proponentFormTypes';
import type {
  BeneficiaryInclusionItem,
  BeneficiaryUpdateItem,
  CalculateProponentPremiumResponse,
  GetLayoutImportSegurVidaParams,
  GetProponentDataParams,
  ImportProponentBatchRequest,
  LayoutImportSegurVidaRetorno,
  ProponentDetail,
} from '../../types/vida/inclusionProponent/inclusionProponentType';
import type { ListaSubestipulanteItem } from '../../types/vida/subEstipulante/subEstipulanteType';
import type { InclusionProponenteTableItem } from '../../types/vida/table/inclusionProponentesTableType';
import {
  buildAddProponentDataRequest,
  buildCalculateProponentPremiumRequest,
  buildProponentListSearchParams,
  buildUpdateProponentDataRequest,
  mapProponentDetailToFormValues,
  mapProponentListItemToTableRow,
  mapUiLayoutTypeToImportCode,
  parseLayoutImportFields,
  parseProponentInconsistenciesFromError,
  ProponentInconsistencyError,
  resolveCodigoUsuarioInclusao,
  resolveLayoutImportParams,
  resolveValorPremioTotal,
} from '../../utils/inclusionProponentUtil';

export type InclusionProponentErrorSource =
  | 'list'
  | 'detail'
  | 'add'
  | 'update'
  | 'remove'
  | 'importLayout'
  | 'importBatch';

export const getInclusionProponentErrorRoute = (
  source: InclusionProponentErrorSource,
): string => {
  switch (source) {
    case 'list':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_LIST_PROPONENTE_MAINTENANCE;
    case 'detail':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_PROPONENTE_MAINTENANCE_DETAIL;
    case 'add':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_ADD_PROPONENTE_MAINTENANCE;
    case 'update':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_ALTER_PROPONENTE_MAINTENANCE;
    case 'remove':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_INATIVATE_PROPONENTE_MAINTENANCE;
    case 'importLayout':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_LAYOUT_IMPORT_SEGUR_VIDA;
    case 'importBatch':
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_IMPORT_PROPONENTE_MAINTENANCE_LOT;
    default:
      return VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_LIST_PROPONENTE_MAINTENANCE;
  }
};

type ApiRequestError = Error & { status?: number };

const getApiErrorStatus = (error: unknown): number | undefined => {
  if (error instanceof Error) {
    return (error as ApiRequestError).status;
  }
  return undefined;
};

const PROPONENT_MUTATION_FALLBACK_MESSAGES = {
  add: 'Não foi possível incluir os dados do proponente.',
  update: 'Não foi possível alterar os dados do proponente.',
} as const;

const resolveProponentMutationErrorMessage = (
  error: unknown,
  operation: keyof typeof PROPONENT_MUTATION_FALLBACK_MESSAGES,
): string => {
  if (!(error instanceof Error)) {
    return PROPONENT_MUTATION_FALLBACK_MESSAGES[operation];
  }

  if (getApiErrorStatus(error) === 400) {
    return PROPONENT_MUTATION_FALLBACK_MESSAGES[operation];
  }

  return error.message || PROPONENT_MUTATION_FALLBACK_MESSAGES[operation];
};

const throwIfProponentInconsistencyOnBadRequest = (error: unknown): void => {
  const status = getApiErrorStatus(error);
  if (status !== 400) {
    return;
  }

  const inconsistencies = parseProponentInconsistenciesFromError(error);
  if (inconsistencies.length > 0) {
    throw new ProponentInconsistencyError(inconsistencies, error);
  }
};

export interface UseInclusionProponentListReturn {
  isLoading: boolean;
  error: string | null;
  rows: InclusionProponenteTableItem[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  clearError: () => void;
  reset: () => void;
  loadProponentList: (
    identSubestipulante: number,
    options?: {
      search?: string;
      page?: number;
      size?: number;
    },
  ) => Promise<void>;
}

export interface UseInclusionProponentDetailReturn {
  isLoading: boolean;
  isSaving: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  error: string | null;
  formValues: ProponentFormValues | null;
  clearError: () => void;
  reset: () => void;
  loadProponentData: (params: GetProponentDataParams) => Promise<void>;
  updateProponentData: (
    formValues: ProponentFormValues,
    beneficiarios?: BeneficiaryUpdateItem[],
  ) => Promise<void>;
  addProponentData: (
    identificadorSubestipulante: number,
    formValues: ProponentFormValues,
    idContrato: number,
    beneficiarios?: BeneficiaryInclusionItem[],
  ) => Promise<void>;
  inactivateProponents: (identProponentes: number[]) => Promise<void>;
}

export interface UseInclusionProponentImportReturn {
  isLoadingLayout: boolean;
  isImporting: boolean;
  layoutError: string | null;
  importError: string | null;
  layoutRetorno: LayoutImportSegurVidaRetorno | null;
  clearLayoutError: () => void;
  clearImportError: () => void;
  loadLayoutImport: (
    subestipulante: ListaSubestipulanteItem,
    layoutType: 'completo' | 'reduzido',
  ) => Promise<void>;
  importProponentBatch: (request: ImportProponentBatchRequest) => Promise<string>;
  resolveUsuarioInclusao: () => string;
}

export interface UseInclusionProponentReturn {
  list: UseInclusionProponentListReturn;
  detail: UseInclusionProponentDetailReturn;
  import: UseInclusionProponentImportReturn;
}

export const useInclusionProponent = (): UseInclusionProponentReturn => {
  const user = useUserStore((state) => state.user);

  const [isListLoading, setIsListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [rows, setRows] = useState<InclusionProponenteTableItem[]>([]);
  const [listCurrentPage, setListCurrentPage] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotalElements, setListTotalElements] = useState(0);

  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<ProponentFormValues | null>(null);
  const [lastParams, setLastParams] = useState<GetProponentDataParams | null>(null);
  const [detail, setDetail] = useState<ProponentDetail | null>(null);

  const [isLoadingLayout, setIsLoadingLayout] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [layoutRetorno, setLayoutRetorno] = useState<LayoutImportSegurVidaRetorno | null>(null);

  const resetList = useCallback(() => {
    setRows([]);
    setListError(null);
    setListCurrentPage(0);
    setListTotalPages(0);
    setListTotalElements(0);
  }, []);

  const clearListError = useCallback(() => {
    setListError(null);
  }, []);

  const loadProponentList = useCallback(async (
    identSubestipulante: number,
    options?: {
      search?: string;
      page?: number;
      size?: number;
    },
  ): Promise<void> => {
    setIsListLoading(true);
    setListError(null);

    try {
      const searchParams = buildProponentListSearchParams(options?.search ?? '');
      const response = await apiService.maintenanceProponent.getProponentList({
        identSubestipulante,
        page: options?.page ?? 0,
        size: options?.size ?? 10,
        ...searchParams,
      });

      setRows(response.pagina.content.map(mapProponentListItemToTableRow));
      setListCurrentPage(response.pagina.pageNumber);
      setListTotalPages(response.pagina.totalPages);
      setListTotalElements(response.pagina.totalElements);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar lista de proponentes';
      setListError(errorMessage);
      setRows([]);
      setListCurrentPage(0);
      setListTotalPages(0);
      setListTotalElements(0);
    } finally {
      setIsListLoading(false);
    }
  }, []);

  const resetDetail = useCallback(() => {
    setFormValues(null);
    setDetail(null);
    setLastParams(null);
    setDetailError(null);
  }, []);

  const clearDetailError = useCallback(() => {
    setDetailError(null);
  }, []);

  const loadProponentData = useCallback(async (
    params: GetProponentDataParams,
  ): Promise<void> => {
    setIsDetailLoading(true);
    setDetailError(null);

    try {
      const response = await apiService.maintenanceProponent.getProponentData(params);
      const loadedDetail = response.retorno?.[0] ?? null;

      if (!loadedDetail) {
        throw new Error('Dados do proponente não encontrados');
      }

      setDetail(loadedDetail);
      setLastParams(params);
      setFormValues(mapProponentDetailToFormValues(loadedDetail));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados do proponente';
      setDetailError(errorMessage);
      setFormValues(null);
      setDetail(null);
      setLastParams(null);
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const updateProponentData = useCallback(async (
    values: ProponentFormValues,
    beneficiarios?: BeneficiaryUpdateItem[],
  ): Promise<void> => {
    if (!detail || !lastParams) {
      throw new Error('Dados do proponente não carregados');
    }

    setIsSaving(true);
    setDetailError(null);

    try {
      const request = buildUpdateProponentDataRequest({
        identificadorSubestipulante: lastParams.identSubestipulante,
        numeroProponente: lastParams.identProponente,
        codigoUsuarioInclusao: resolveCodigoUsuarioInclusao(user?.idPessoa),
        formValues: values,
        detail,
        beneficiarios,
      });

      const response = await apiService.maintenanceProponent.updateProponentData(request);

      if (response.retorno?.tipoInconsistencia) {
        throw new ProponentInconsistencyError([response.retorno]);
      }
    } catch (err) {
      throwIfProponentInconsistencyOnBadRequest(err);

      const errorMessage = resolveProponentMutationErrorMessage(err, 'update');
      setDetailError(errorMessage);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [detail, lastParams, user?.idPessoa]);

  const addProponentData = useCallback(async (
    identificadorSubestipulante: number,
    values: ProponentFormValues,
    idContrato: number,
    beneficiarios?: BeneficiaryInclusionItem[],
  ): Promise<void> => {
    setIsAdding(true);
    setDetailError(null);

    try {
      const codigoUsuarioInclusao = resolveCodigoUsuarioInclusao(user?.idPessoa);
      const idProtocolo =
        useVidaProtocolAtendimentoStore.getState().attendanceProtocol?.idProtocolo ?? 0;

      const premiumRequest = buildCalculateProponentPremiumRequest({
        idContrato,
        idSubestipulante: String(identificadorSubestipulante),
        idProtocolo,
        formValues: values,
      });

      const premiumRaw = await apiService.maintenanceProponent.calculateProponentPremium(
        premiumRequest,
      ) as CalculateProponentPremiumResponse | CalculateProponentPremiumResponse[];

      const valorPremioTotal = resolveValorPremioTotal(premiumRaw);

      const request = buildAddProponentDataRequest({
        identificadorSubestipulante,
        codigoUsuarioInclusao,
        formValues: values,
        vrPremioSeg: String(valorPremioTotal),
        beneficiarios,
      });

      const response = await apiService.maintenanceProponent.addProponentData(request);
      if (response.retorno?.tipoInconsistencia) {
        throw new ProponentInconsistencyError([response.retorno]);
      }
    } catch (err) {
      throwIfProponentInconsistencyOnBadRequest(err);

      const errorMessage = resolveProponentMutationErrorMessage(err, 'add');
      setDetailError(errorMessage);
      throw err;
    } finally {
      setIsAdding(false);
    }
  }, [user?.idPessoa]);

  const inactivateProponents = useCallback(async (
    identProponentes: number[],
  ): Promise<void> => {
    if (identProponentes.length === 0) {
      return;
    }

    setIsDeleting(true);
    setDetailError(null);

    try {
      for (const identProponente of identProponentes) {
        const response = await apiService.maintenanceProponent.inactivateProponentData({
          identProponente,
        });

        if (response.retorno?.tipoInconsistencia) {
          throw new Error(
            response.mensagem ?? 'Não foi possível excluir o proponente',
          );
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir proponente';
      setDetailError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const clearLayoutError = useCallback(() => {
    setLayoutError(null);
  }, []);

  const clearImportError = useCallback(() => {
    setImportError(null);
  }, []);

  const resolveUsuarioInclusao = useCallback(
    () => resolveCodigoUsuarioInclusao(user?.idPessoa),
    [user?.idPessoa],
  );

  const loadLayoutImport = useCallback(async (
    subestipulante: ListaSubestipulanteItem,
    layoutType: 'completo' | 'reduzido',
  ): Promise<void> => {
    setIsLoadingLayout(true);
    setLayoutError(null);

    try {
      const { pcContrApolc, pcSubEstteApolc } = resolveLayoutImportParams(subestipulante);
      const params: GetLayoutImportSegurVidaParams = {
        pcContrApolc,
        pcSubEstteApolc,
        pcTpoLyoutImporSegurVida: mapUiLayoutTypeToImportCode(layoutType),
      };

      const response = await apiService.maintenanceProponent.getLayoutImportSegurVida(params);
      setLayoutRetorno(response.retorno ?? null);

      const parsedFields = parseLayoutImportFields(response.retorno?.layouts);
      if (!parsedFields && response.retorno?.layouts) {
        setLayoutError('Não foi possível interpretar o layout retornado pela API.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Erro ao buscar layout de importação';
      setLayoutError(errorMessage);
      setLayoutRetorno(null);
    } finally {
      setIsLoadingLayout(false);
    }
  }, []);

  const importProponentBatch = useCallback(async (
    request: ImportProponentBatchRequest,
  ): Promise<string> => {
    setIsImporting(true);
    setImportError(null);

    try {
      const response = await apiService.maintenanceProponent.importProponentBatch(request);

      if (response.retorno?.geraNotificacao) {
        throw new Error(response.retorno.geraNotificacao);
      }

      return response.mensagem ?? 'Importação realizada com sucesso.';
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Erro ao importar proponentes';
      setImportError(errorMessage);
      throw err;
    } finally {
      setIsImporting(false);
    }
  }, []);

  return {
    list: {
      isLoading: isListLoading,
      error: listError,
      rows,
      currentPage: listCurrentPage,
      totalPages: listTotalPages,
      totalElements: listTotalElements,
      clearError: clearListError,
      reset: resetList,
      loadProponentList,
    },
    detail: {
      isLoading: isDetailLoading,
      isSaving,
      isAdding,
      isDeleting,
      error: detailError,
      formValues,
      clearError: clearDetailError,
      reset: resetDetail,
      loadProponentData,
      updateProponentData,
      addProponentData,
      inactivateProponents,
    },
    import: {
      isLoadingLayout,
      isImporting,
      layoutError,
      importError,
      layoutRetorno,
      clearLayoutError,
      clearImportError,
      loadLayoutImport,
      importProponentBatch,
      resolveUsuarioInclusao,
    },
  };
};
